import type {
  BulkMetricUpdateApiItem,
  MetricSimplifiedRow,
  SensitivityLevels,
} from "./SiteAboutPageUtils/bulkUpdateCamerasApi";
import { getLabelNameActiveMapForCameraFromSimplifiedRows } from "./SiteAboutPageUtils/metricSimplifiedUtils";

export type BulkEnableOption = "no_change" | "enable" | "disable";
export type BulkSensitivityOption =
  | "no_change"
  | "low"
  | "medium"
  | "medium_high"
  | "high";

export interface ProductBulkFormRow {
  key: string;
  label: string;
  supportsSensitivity: boolean;
  supportsLoiteringSeconds?: boolean;
}

export interface ProductBulkFormState {
  [key: string]: {
    enable: BulkEnableOption;
    sensitivity: BulkSensitivityOption;
    loiteringSeconds: string;
  };
}

export interface BulkProductChangePayload {
  product: string;
  enable?: "enable" | "disable";
  sensitivity?: "low" | "medium" | "medium_high" | "high";
  loitering_seconds?: number;
}

export const PRODUCT_BULK_ROWS: ProductBulkFormRow[] = [
  { key: "intruder", label: "Intruder", supportsSensitivity: true },
  { key: "bike", label: "Bike", supportsSensitivity: true },
  { key: "vehicle", label: "Vehicle", supportsSensitivity: true },
  {
    key: "intruder_motion",
    label: "Intruder Motion",
    supportsSensitivity: true,
  },
  {
    key: "vehicle_motion",
    label: "Vehicle Motion",
    supportsSensitivity: true,
  },
  { key: "bike_motion", label: "Bike Motion", supportsSensitivity: true },
  { key: "gun", label: "Gun", supportsSensitivity: true },
  { key: "pistol", label: "Pistol", supportsSensitivity: true },
  { key: "crowd", label: "Crowd", supportsSensitivity: true },
  {
    key: "loiterer",
    label: "Loiterer",
    supportsSensitivity: true,
    supportsLoiteringSeconds: true,
  },
  {
    key: "vehicle_loiterer",
    label: "Vehicle Loiterer",
    supportsSensitivity: true,
    supportsLoiteringSeconds: true,
  },
  { key: "no_hard_hat", label: "No Hard Hat", supportsSensitivity: true },
  { key: "fire", label: "Fire", supportsSensitivity: true },
  { key: "smoke", label: "Smoke", supportsSensitivity: true },
  {
    key: "intruder_line_crossing",
    label: "Intruder Line Crossing",
    supportsSensitivity: true,
  },
  {
    key: "vehicle_line_crossing",
    label: "Vehicle Line Crossing",
    supportsSensitivity: true,
  },
  { key: "amazon", label: "Amazon", supportsSensitivity: true },
  { key: "dhl", label: "DHL", supportsSensitivity: true },
  { key: "fedex", label: "Fedex", supportsSensitivity: true },
  { key: "school_bus", label: "School Bus", supportsSensitivity: true },
  { key: "ups", label: "UPS", supportsSensitivity: true },
  { key: "usps", label: "USPS", supportsSensitivity: true },
  { key: "envera", label: "Envera", supportsSensitivity: true },
  { key: "fire_truck", label: "Fire Truck", supportsSensitivity: true },
  { key: "non_ups", label: "Non-UPS", supportsSensitivity: true },
];

/** Products not available on autopatrol cameras — excluded from autopatrol bulk update UI. */
export const AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_KEYS = [
  "intruder_motion",
  "vehicle_motion",
  "bike_motion",
  "gun",
  "pistol",
  "loiterer",
  "vehicle_loiterer",
  "no_hard_hat",
  "intruder_line_crossing",
  "vehicle_line_crossing",
] as const;

const AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_KEY_SET = new Set<string>(
  AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_KEYS
);

export const AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_LABELS =
  PRODUCT_BULK_ROWS.filter((row) =>
    AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_KEY_SET.has(row.key)
  ).map((row) => row.label);

export const AUTOPATROL_PRODUCT_BULK_ROWS = PRODUCT_BULK_ROWS.filter(
  (row) => !AUTOPATROL_UNAVAILABLE_BULK_PRODUCT_KEY_SET.has(row.key)
);

export const createInitialProductBulkFormState = (
  rows: ProductBulkFormRow[] = PRODUCT_BULK_ROWS
): ProductBulkFormState =>
  rows.reduce<ProductBulkFormState>((accumulator, row) => {
    accumulator[row.key] = {
      enable: "no_change",
      sensitivity: "no_change",
      loiteringSeconds: "",
    };
    return accumulator;
  }, {});

const POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;

export const validateLoiteringSeconds = (
  rows: ProductBulkFormRow[],
  state: ProductBulkFormState
): Record<string, string> => {
  const errors: Record<string, string> = {};

  rows.forEach((row) => {
    if (!row.supportsLoiteringSeconds) return;
    const value = state[row.key]?.loiteringSeconds?.trim() ?? "";
    if (!value) return;

    if (!POSITIVE_INTEGER_PATTERN.test(value)) {
      errors[row.key] = "Enter a positive whole number";
    }
  });

  return errors;
};

export const buildBulkProductChangePayload = (
  rows: ProductBulkFormRow[],
  state: ProductBulkFormState
): BulkProductChangePayload[] => {
  const payload: BulkProductChangePayload[] = [];

  rows.forEach((row) => {
    const rowState = state[row.key];
    if (!rowState) return;

    const entry: BulkProductChangePayload = {
      product: row.key,
    };

    if (rowState.enable !== "no_change") {
      entry.enable = rowState.enable;
    }

    if (row.supportsSensitivity && rowState.sensitivity !== "no_change") {
      entry.sensitivity = rowState.sensitivity;
    }

    if (row.supportsLoiteringSeconds) {
      const value = rowState.loiteringSeconds.trim();
      if (value && POSITIVE_INTEGER_PATTERN.test(value)) {
        entry.loitering_seconds = Number(value);
      }
    }

    if (
      entry.enable !== undefined ||
      entry.sensitivity !== undefined ||
      entry.loitering_seconds !== undefined
    ) {
      payload.push(entry);
    }
  });

  return payload;
};

/** Intruder, Bike, Vehicle — mutually exclusive with Motion Plus and Line Crossing (Edit Camera Products tab). */
export const BULK_INTRUDER_GROUP_KEYS = [
  "intruder",
  "bike",
  "vehicle",
] as const;

export const BULK_MOTION_PLUS_GROUP_KEYS = [
  "intruder_motion",
  "vehicle_motion",
  "bike_motion",
] as const;

export const BULK_LINE_CROSSING_GROUP_KEYS = [
  "intruder_line_crossing",
  "vehicle_line_crossing",
] as const;

export type MutuallyExclusiveBulkGroup =
  | "intruder"
  | "motionPlus"
  | "lineCrossing";

const KEY_TO_EXCLUSIVE_GROUP: Record<string, MutuallyExclusiveBulkGroup> =
  Object.fromEntries([
    ...BULK_INTRUDER_GROUP_KEYS.map((k) => [k, "intruder" as const]),
    ...BULK_MOTION_PLUS_GROUP_KEYS.map((k) => [k, "motionPlus" as const]),
    ...BULK_LINE_CROSSING_GROUP_KEYS.map((k) => [k, "lineCrossing" as const]),
  ]);

export const getMutuallyExclusiveBulkGroupForKey = (
  labelKey: string
): MutuallyExclusiveBulkGroup | null => KEY_TO_EXCLUSIVE_GROUP[labelKey] ?? null;

const rowByKey = new Map(
  PRODUCT_BULK_ROWS.map((row) => [row.key, row] as const)
);

/**
 * Effective active state for one product on one camera after applying bulk form (merge with current UI state).
 */
export const isBulkProductActiveAfterMerge = (
  labelKey: string,
  cameraId: number,
  state: ProductBulkFormState,
  activeCameraProducts: { [id: number]: { [productName: string]: boolean } }
): boolean => {
  const row = rowByKey.get(labelKey);
  if (!row) return false;
  const rowState = state[labelKey];
  if (!rowState) return false;
  if (rowState.enable === "enable") return true;
  if (rowState.enable === "disable") return false;
  return activeCameraProducts[cameraId]?.[row.label] === true;
};

/**
 * Returns an error message if any selected camera would have more than one of
 * Intruder group / Motion Plus / Line Crossing active after this bulk update.
 */
export type BulkExclusiveGroupFlags = {
  intruderGroup: boolean;
  motionPlus: boolean;
  lineCrossing: boolean;
};

/** Per-camera merged flags for the three mutually exclusive product groups (Edit Camera products tab). */
export const getBulkExclusiveGroupFlagsForCamera = (
  cameraId: number,
  state: ProductBulkFormState,
  activeCameraProducts: { [id: number]: { [productName: string]: boolean } }
): BulkExclusiveGroupFlags => ({
  intruderGroup: BULK_INTRUDER_GROUP_KEYS.some((key) =>
    isBulkProductActiveAfterMerge(key, cameraId, state, activeCameraProducts)
  ),
  motionPlus: BULK_MOTION_PLUS_GROUP_KEYS.some((key) =>
    isBulkProductActiveAfterMerge(key, cameraId, state, activeCameraProducts)
  ),
  lineCrossing: BULK_LINE_CROSSING_GROUP_KEYS.some((key) =>
    isBulkProductActiveAfterMerge(key, cameraId, state, activeCameraProducts)
  ),
});

/** User chose Enable on any Intruder / Bike / Vehicle row in the bulk form. */
export const userBulkSelectedEnableIntruderGroup = (
  state: ProductBulkFormState
): boolean =>
  BULK_INTRUDER_GROUP_KEYS.some((key) => state[key]?.enable === "enable");

/** User chose Enable on any Motion Plus row in the bulk form. */
export const userBulkSelectedEnableMotionPlusGroup = (
  state: ProductBulkFormState
): boolean =>
  BULK_MOTION_PLUS_GROUP_KEYS.some((key) => state[key]?.enable === "enable");

/** User chose Enable on any Line Crossing row in the bulk form. */
export const userBulkSelectedEnableLineCrossingGroup = (
  state: ProductBulkFormState
): boolean =>
  BULK_LINE_CROSSING_GROUP_KEYS.some((key) => state[key]?.enable === "enable");

/**
 * Enable, sensitivity, and loitering for this row are replaced with messaging when another
 * exclusive group has "Enable" in this bulk form. Rows in a group stay editable if that
 * group already has any "Enable" selected (so the user can revert). On first load, nothing
 * is restricted.
 */
export const isBulkRowRestrictedByPeerEnableSelection = (
  labelKey: string,
  state: ProductBulkFormState
): boolean => {
  const group = getMutuallyExclusiveBulkGroupForKey(labelKey);
  if (!group) {
    return false;
  }
  const ui = userBulkSelectedEnableIntruderGroup(state);
  const um = userBulkSelectedEnableMotionPlusGroup(state);
  const ul = userBulkSelectedEnableLineCrossingGroup(state);
  if (group === "intruder") {
    if (ui) {
      return false;
    }
    return um || ul;
  }
  if (group === "motionPlus") {
    if (um) {
      return false;
    }
    return ui || ul;
  }
  if (ul) {
    return false;
  }
  return ui || um;
};

/**
 * Copy when Enable/Sensitivity/Loitering are hidden due to a peer group's Enable selection
 * (same conflicts as Edit Camera Products tab).
 */
export const getBulkPeerEnableBlockedExplanation = (
  labelKey: string,
  state: ProductBulkFormState
): string | null => {
  if (!isBulkRowRestrictedByPeerEnableSelection(labelKey, state)) {
    return null;
  }
  const group = getMutuallyExclusiveBulkGroupForKey(labelKey);
  if (!group) {
    return null;
  }
  const ui = userBulkSelectedEnableIntruderGroup(state);
  const um = userBulkSelectedEnableMotionPlusGroup(state);
  const ul = userBulkSelectedEnableLineCrossingGroup(state);

  if (group === "motionPlus") {
    if (ui && ul) {
      return (
        "You selected Enable for Intruder (Intruder, Bike, or Vehicle) and for Line Crossing in this bulk update. " +
        "Motion Plus cannot run with either. Set those rows to No Change or Disable to configure Motion Plus here."
      );
    }
    if (ui) {
      return (
        "You selected Enable for Intruder (Intruder, Bike, or Vehicle) in this bulk update. " +
        "Intruder and Motion Plus cannot run together. Set Intruder group rows to No Change or Disable to configure Motion Plus here."
      );
    }
    return (
      "You selected Enable for Line Crossing in this bulk update. " +
      "Line Crossing and Motion Plus cannot run together. Set Line Crossing rows to No Change or Disable to configure Motion Plus here."
    );
  }

  if (group === "lineCrossing") {
    if (ui && um) {
      return (
        "You selected Enable for Intruder and for Motion Plus in this bulk update. " +
        "Line Crossing cannot run with either. Set those rows to No Change or Disable to configure Line Crossing here."
      );
    }
    if (ui) {
      return (
        "You selected Enable for Intruder (Intruder, Bike, or Vehicle) in this bulk update. " +
        "Intruder and Line Crossing cannot run together. Set Intruder group rows to No Change or Disable to configure Line Crossing here."
      );
    }
    return (
      "You selected Enable for Motion Plus in this bulk update. " +
      "Motion Plus and Line Crossing cannot run together. Set Motion Plus rows to No Change or Disable to configure Line Crossing here."
    );
  }

  if (um && ul) {
    return (
      "You selected Enable for Motion Plus and for Line Crossing in this bulk update. " +
      "Intruder (person / vehicle / bike detection) cannot run with either. Set those rows to No Change or Disable to configure Intruder, Bike, or Vehicle here."
    );
  }
  if (um) {
    return (
      "You selected Enable for Motion Plus in this bulk update. " +
      "Motion Plus and Intruder cannot run together. Set Motion Plus rows to No Change or Disable to configure Intruder, Bike, or Vehicle here."
    );
  }
  return (
    "You selected Enable for Line Crossing in this bulk update. " +
    "Line Crossing and Intruder cannot run together. Set Line Crossing rows to No Change or Disable to configure Intruder, Bike, or Vehicle here."
  );
};

export const validateMutuallyExclusiveProductGroupsForCameras = (
  selectedCameraIds: number[],
  state: ProductBulkFormState,
  activeCameraProducts: { [id: number]: { [productName: string]: boolean } }
): string | null => {
  const uiSel = userBulkSelectedEnableIntruderGroup(state);
  const umSel = userBulkSelectedEnableMotionPlusGroup(state);
  const ulSel = userBulkSelectedEnableLineCrossingGroup(state);

  const exclusiveConflictMessage =
    "Intruder (Intruder, Bike, Vehicle), Motion Plus (Intruder Motion, Vehicle Motion, Bike Motion), " +
    "and Line Crossing (Intruder Line Crossing, Vehicle Line Crossing) cannot be enabled at the same time on a camera. " +
    "Adjust enable settings or disable one group first (same rules as the Edit Camera products tab).";

  if ([uiSel, umSel, ulSel].filter(Boolean).length > 1) {
    return exclusiveConflictMessage;
  }

  for (const cameraId of selectedCameraIds) {
    let intruderGroupActive = BULK_INTRUDER_GROUP_KEYS.some((key) =>
      isBulkProductActiveAfterMerge(key, cameraId, state, activeCameraProducts)
    );
    let motionPlusActive = BULK_MOTION_PLUS_GROUP_KEYS.some((key) =>
      isBulkProductActiveAfterMerge(key, cameraId, state, activeCameraProducts)
    );
    let lineCrossingActive = BULK_LINE_CROSSING_GROUP_KEYS.some((key) =>
      isBulkProductActiveAfterMerge(key, cameraId, state, activeCameraProducts)
    );

    /** Match `mergeImplicitExclusiveGroupDisablesIntoBulkPayload`: chosen Enable group turns conflicting groups off in the API. */
    if (uiSel) {
      motionPlusActive = false;
      lineCrossingActive = false;
    } else if (umSel) {
      intruderGroupActive = false;
      lineCrossingActive = false;
    } else if (ulSel) {
      intruderGroupActive = false;
      motionPlusActive = false;
    }

    const activeCount = [intruderGroupActive, motionPlusActive, lineCrossingActive].filter(
      Boolean
    ).length;

    if (activeCount > 1) {
      return exclusiveConflictMessage;
    }
  }
  return null;
};

export const findMissingSensitivityIds = (
  rows: ProductBulkFormRow[],
  state: ProductBulkFormState,
  sensitivityMap: Map<string, SensitivityLevels>
): string[] => {
  const missing: string[] = [];
  rows.forEach((row) => {
    const rowState = state[row.key];
    if (!row.supportsSensitivity || !rowState || rowState.sensitivity === "no_change") {
      return;
    }
    const levels = sensitivityMap.get(row.key);
    const id = levels?.[rowState.sensitivity];
    if (id === undefined || id === null) {
      missing.push(row.label);
    }
  });
  return missing;
};

/**
 * Builds POST /metric/bulk_update/ items; use `null` fields for no change on that attribute.
 */
export const buildMetricBulkUpdateApiPayload = (
  rows: ProductBulkFormRow[],
  state: ProductBulkFormState,
  sensitivityMap: Map<string, SensitivityLevels>
): BulkMetricUpdateApiItem[] => {
  const items: BulkMetricUpdateApiItem[] = [];

  rows.forEach((row) => {
    const rowState = state[row.key];
    if (!rowState) return;

    let active: boolean | null = null;
    if (rowState.enable === "enable") {
      active = true;
    } else if (rowState.enable === "disable") {
      active = false;
    }

    let sensitivity_id: number | null = null;
    if (row.supportsSensitivity && rowState.sensitivity !== "no_change") {
      const levels = sensitivityMap.get(row.key);
      const resolved = levels?.[rowState.sensitivity];
      if (resolved !== undefined && resolved !== null) {
        sensitivity_id = resolved;
      }
    }

    let frame_thresh: number | null = null;
    const min_crowd_size: number | null = null;
    if (row.supportsLoiteringSeconds) {
      const value = rowState.loiteringSeconds.trim();
      if (value && POSITIVE_INTEGER_PATTERN.test(value)) {
        frame_thresh = Number(value);
      }
    }

    const hasChange =
      active !== null ||
      sensitivity_id !== null ||
      frame_thresh !== null ||
      min_crowd_size !== null;

    if (!hasChange) return;

    items.push({
      label_name: row.key,
      active,
      sensitivity_id,
      frame_thresh,
      min_crowd_size,
    });
  });

  return items;
};

const IMPLICIT_DISABLE_ITEM = (
  labelKey: string
): BulkMetricUpdateApiItem => ({
  label_name: labelKey,
  active: false,
  sensitivity_id: null,
  frame_thresh: null,
  min_crowd_size: null,
});

/**
 * When the user sets any row in an exclusive group to Enable, append API rows that
 * explicitly disable all conflicting metrics (same mutual-exclusion rules as Edit Camera).
 * Merges with existing payload entries by label_name so explicit form choices stay
 * deduplicated; conflicting enables are overwritten with disable for safety.
 */
export const mergeImplicitExclusiveGroupDisablesIntoBulkPayload = (
  state: ProductBulkFormState,
  items: BulkMetricUpdateApiItem[]
): BulkMetricUpdateApiItem[] => {
  const byLabel = new Map<string, BulkMetricUpdateApiItem>();
  items.forEach((item) => {
    byLabel.set(item.label_name, { ...item });
  });

  const forceDisableKeys = (keys: readonly string[]) => {
    keys.forEach((key) => {
      byLabel.set(key, IMPLICIT_DISABLE_ITEM(key));
    });
  };

  if (userBulkSelectedEnableIntruderGroup(state)) {
    forceDisableKeys([...BULK_MOTION_PLUS_GROUP_KEYS, ...BULK_LINE_CROSSING_GROUP_KEYS]);
  }
  if (userBulkSelectedEnableMotionPlusGroup(state)) {
    forceDisableKeys([...BULK_INTRUDER_GROUP_KEYS, ...BULK_LINE_CROSSING_GROUP_KEYS]);
  }
  if (userBulkSelectedEnableLineCrossingGroup(state)) {
    forceDisableKeys([...BULK_INTRUDER_GROUP_KEYS, ...BULK_MOTION_PLUS_GROUP_KEYS]);
  }

  const ordered: BulkMetricUpdateApiItem[] = [];
  const seen = new Set<string>();
  PRODUCT_BULK_ROWS.forEach((row) => {
    const item = byLabel.get(row.key);
    if (item) {
      ordered.push(item);
      seen.add(row.key);
    }
  });
  byLabel.forEach((item, key) => {
    if (!seen.has(key)) {
      ordered.push(item);
    }
  });
  return ordered;
};

/**
 * Expected active flag after bulk apply (form + pre-apply snapshot + same implicit disables as the API payload).
 */
export const isBulkProductExpectedActiveAfterBulkApply = (
  labelKey: string,
  cameraId: number,
  state: ProductBulkFormState,
  mergeSnapshot: { [cameraId: number]: { [productName: string]: boolean } | undefined }
): boolean => {
  const ui = userBulkSelectedEnableIntruderGroup(state);
  const um = userBulkSelectedEnableMotionPlusGroup(state);
  const ul = userBulkSelectedEnableLineCrossingGroup(state);

  if (ui) {
    if (
      (BULK_MOTION_PLUS_GROUP_KEYS as readonly string[]).includes(labelKey) ||
      (BULK_LINE_CROSSING_GROUP_KEYS as readonly string[]).includes(labelKey)
    ) {
      return false;
    }
  }
  if (um) {
    if (
      (BULK_INTRUDER_GROUP_KEYS as readonly string[]).includes(labelKey) ||
      (BULK_LINE_CROSSING_GROUP_KEYS as readonly string[]).includes(labelKey)
    ) {
      return false;
    }
  }
  if (ul) {
    if (
      (BULK_INTRUDER_GROUP_KEYS as readonly string[]).includes(labelKey) ||
      (BULK_MOTION_PLUS_GROUP_KEYS as readonly string[]).includes(labelKey)
    ) {
      return false;
    }
  }

  return isBulkProductActiveAfterMerge(
    labelKey,
    cameraId,
    state,
    mergeSnapshot as { [id: number]: { [productName: string]: boolean } }
  );
};

/**
 * True when simplified metric rows match the post-bulk state implied by the form + pre-apply product map.
 */
export const simplifiedMetricRowsMatchBulkFormExpectations = (
  rows: MetricSimplifiedRow[],
  selectedCameraIds: number[],
  state: ProductBulkFormState,
  mergeSnapshot: { [cameraId: number]: { [productName: string]: boolean } | undefined },
  productRows: ProductBulkFormRow[] = PRODUCT_BULK_ROWS
): boolean => {
  for (const cameraId of selectedCameraIds) {
    const labelMap = getLabelNameActiveMapForCameraFromSimplifiedRows(rows, cameraId);
    for (const row of productRows) {
      const expected = isBulkProductExpectedActiveAfterBulkApply(
        row.key,
        cameraId,
        state,
        mergeSnapshot
      );
      const actual = labelMap.get(row.key) === true;
      if (expected !== actual) {
        return false;
      }
    }
  }
  return true;
};
